/**
 * CharacterSheetPage
 *
 * Standalone page rendered in a separate browser window.
 * Loads a single character from Supabase and renders the full sheet.
 * Dice rolls are relayed to the main window via BroadcastChannel so the
 * 3D dice animate on the game board. Chat posting syncs via Supabase.
 * Token store is initialized so Echo Knight controls work from popup.
 */

import { useEffect, useRef } from 'react';
import { useAuthStore } from '../stores/authStore';
import { useSessionStore } from '../stores/sessionStore';
import { useCampaignStore } from '../stores/campaignStore';
import { useCharacterStore } from '../stores/characterStore';
import { useChatStore } from '../stores/chatStore';
import { useDiceStore } from '../stores/diceStore';
import { useTokenStore } from '../stores/tokenStore';
import { supabase } from '../lib/supabase';
import { FullCharacterSheet } from './FullCharacterSheet';
import type { DiceResult } from './DiceRoller';
import { Icon } from './Icon';

const DICE_CHANNEL = 'machina-dice';

interface CharacterSheetPageProps {
  campaignId: string;
  characterId: string;
}

export function CharacterSheetPage({ campaignId, characterId }: CharacterSheetPageProps) {
  const { user } = useAuthStore();
  const initializeUser = useSessionStore((s) => s.initializeUser);
  const userId = useSessionStore((s) => s.userId);
  const isInitialized = useSessionStore((s) => s.isInitialized);
  const setCurrentCampaign = useCampaignStore((s) => s.setCurrentCampaign);

  const loadFromSupabase = useCharacterStore((s) => s.loadFromSupabase);
  const subscribeToRealtime = useCharacterStore((s) => s.subscribeToRealtime);
  const unsubscribeFromRealtime = useCharacterStore((s) => s.unsubscribeFromRealtime);
  const character = useCharacterStore((s) => s.characters.get(characterId));

  const subscribeToChatRealtime = useChatStore((s) => s.subscribeToRealtime);
  const loadChatFromSupabase = useChatStore((s) => s.loadFromSupabase);
  const unsubscribeFromChatRealtime = useChatStore((s) => s.unsubscribeFromRealtime);
  const setCurrentUser = useChatStore((s) => s.setCurrentUser);

  const setRollFn = useDiceStore((s) => s.setRollFn);

  const hasInitRef = useRef(false);
  const pendingRolls = useRef<Map<string, (result: DiceResult) => void>>(new Map());

  // Set up BroadcastChannel to relay dice rolls to main window
  useEffect(() => {
    const channel = new BroadcastChannel(DICE_CHANNEL);

    // Listen for roll results coming back from the main window
    channel.onmessage = (event) => {
      if (event.data?.type === 'DICE_ROLL_RESULT') {
        const { requestId, result } = event.data;
        const resolve = pendingRolls.current.get(requestId);
        if (resolve) {
          pendingRolls.current.delete(requestId);
          resolve(result);
        }
      }
    };

    // Register a rollFn that sends requests to the main window
    const remoteRoll = (notation: string): Promise<DiceResult> => {
      return new Promise((resolve) => {
        const requestId = crypto.randomUUID();
        pendingRolls.current.set(requestId, resolve);
        channel.postMessage({
          type: 'DICE_ROLL_REQUEST',
          requestId,
          notation,
        });
      });
    };

    setRollFn(remoteRoll);

    return () => {
      channel.close();
    };
  }, [setRollFn]);

  // Step 1: Initialize session
  useEffect(() => {
    if (hasInitRef.current) return;
    hasInitRef.current = true;
    initializeUser();
  }, [initializeUser]);

  // Step 2: Once user is ready, set campaign and load character data
  useEffect(() => {
    if (!userId || !isInitialized) return;

    setCurrentCampaign(campaignId);
    loadFromSupabase(userId, campaignId);

    // Subscribe to character realtime updates (HP changes, etc.)
    subscribeToRealtime(campaignId);

    // Subscribe to chat so "share to chat" works
    subscribeToChatRealtime(campaignId);
    loadChatFromSupabase(campaignId);
    setCurrentUser(userId);

    // Load token store so Echo Knight controls work from popup window.
    // Query the active scene, then load + subscribe to tokens.
    const initTokens = async () => {
      const { data: scenes } = await supabase
        .from('machina_scenes')
        .select('id')
        .eq('campaign_id', campaignId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1);

      const sceneId = scenes?.[0]?.id;
      if (sceneId) {
        const tokenStore = useTokenStore.getState();
        await tokenStore.loadFromSupabase(sceneId);
        tokenStore.subscribeToRealtime(sceneId);
      }
    };
    initTokens();

    return () => {
      unsubscribeFromRealtime();
      unsubscribeFromChatRealtime();
      useTokenStore.getState().unsubscribeFromRealtime();
    };
  }, [userId, isInitialized, campaignId, setCurrentCampaign, loadFromSupabase, subscribeToRealtime, unsubscribeFromRealtime, subscribeToChatRealtime, loadChatFromSupabase, unsubscribeFromChatRealtime, setCurrentUser]);

  // Set window title
  useEffect(() => {
    if (character) {
      document.title = `${character.name} — Machina`;
    }
  }, [character?.name]);

  // Not logged in
  if (!user) {
    return (
      <div style={styles.loadingContainer}>
        <Icon name="lock" size={32} color="rgba(192, 192, 192, 0.4)" />
        <div style={styles.loadingText}>Not signed in</div>
        <div style={styles.loadingSubtext}>Open Machina in another tab and sign in first.</div>
      </div>
    );
  }

  // Loading
  if (!character) {
    return (
      <div style={styles.loadingContainer}>
        <Icon name="loader" size={32} color="#d4af37" />
        <div style={styles.loadingText}>Loading character sheet...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <FullCharacterSheet character={character} />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'var(--color-background)',
    color: 'var(--color-text)',
    overflow: 'hidden',
  },
  loadingContainer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    backgroundColor: 'var(--color-background)',
  },
  loadingText: {
    fontSize: 14,
    color: 'var(--color-text-secondary)',
    fontFamily: 'Georgia, serif',
  },
  loadingSubtext: {
    fontSize: 12,
    color: 'var(--color-text-muted)',
    fontFamily: 'Georgia, serif',
    fontStyle: 'italic',
  },
};
